import {
  Button,
  ButtonGroup,
  Card,
  Center,
  Flex,
  Grid,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Control,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { Link } from "react-router";

import { Restricted } from "@/app/components/Restricted";
import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import FormReset from "@/common/components/Form/FormReset";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { RulesDisplay, RulesForm } from "@/common/components/Rule/RulesForm";
import { RuleSimpleCompanySelector } from "@/common/components/Rule/RuleSimpleCompanySelector";
import { RuleSimpleUserSelector } from "@/common/components/Rule/RuleSimpleUserSelector";
import { SectionHeading } from "@/common/components/SectionHeading";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useUnsavedChangesRegister } from "@/common/contexts/UnsavedChangesContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { FeatureTargetingChangeConfirmationDialog } from "@/feature/components/FeatureTargetingChangeConfirmationDialog";
import { FeatureTargetingHistory } from "@/feature/components/FeatureTargetingHistory";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import { useFeatureOptInMembershipsData } from "@/feature/data/useFeatureOptInMembershipsData";
import { useFeatureTargetingData } from "@/feature/data/useFeatureTargetingData";
import { useFeatureTargetingUpdateMutation } from "@/feature/data/useFeatureTargetingUpdateMutation";
import {
  getDefaultNotificationProvidersForTargetingChange,
  getTargetingNotificationDefaultsInfo,
} from "@/feature/utils/targetingNotificationDefaults";
import { useTargetingNotificationSettings } from "@/integration/data/useTargetingNotificationSettings";
import { useStagesData } from "@/stage/data/useStagesData";
import { useFlag, useRequestFeedback } from "@reflag/react-sdk";
import { ErrorResponse } from "@reflag/shared/api";
import { FeatureDetail, FeatureName } from "@reflag/shared/featureAPI";
import {
  FEATURE_TARGETING_VERSION_CONFLICT_MESSAGE,
  FeatureOptInMemberships,
  FeatureTargetingVersion,
  UpdateEnvironmentFeatureTargeting,
  UpdateFeatureTargetingBody,
  UpdateFeatureTargetingBodySchema,
} from "@reflag/shared/featureTargetingAPI";
import { StageDTO } from "@reflag/shared/stageAPI";
import {
  EnvironmentFeatureTargeting,
  expandEnvironmentTargeting,
  updateToEnvironmentFeatureTargeting,
} from "@reflag/shared/utils/targetingRule";

type EnvironmentFormProps = {
  feature: FeatureName;
  baseName: `versions.${number}`;
  control: Control<UpdateFeatureTargetingBody>;
  additionalRuleContent?: ReactNode;
  optInMemberships?: FeatureOptInMemberships;
};

const EnvironmentForm = ({
  feature,
  baseName,
  control,
  additionalRuleContent,
  optInMemberships,
}: EnvironmentFormProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${baseName}.customRules`,
  });

  return (
    <RulesForm
      additionalRuleContent={additionalRuleContent}
      optInMemberships={optInMemberships}
      append={append}
      baseName={baseName}
      feature={feature}
      fields={fields}
      remove={remove}
    />
  );
};

function OptInTargetsFrame({
  canEdit,
  companyIds,
  userIds,
}: {
  canEdit: boolean;
  companyIds: string[];
  userIds: string[];
}) {
  const hasOptedInTargets = userIds.length > 0 || companyIds.length > 0;

  return (
    <Card p={4} w="full">
      <Grid
        alignItems="center"
        gap={2}
        templateColumns="80px minmax(0, 1fr) auto"
      >
        <Text fontSize="sm">Opted in</Text>
        <Flex align="center" gap={2} minH={8} minW={0} wrap="wrap">
          {userIds.length > 0 && (
            <RuleSimpleUserSelector
              allowReadOnlyOpen
              isReadOnly
              summaryCount={10}
              value={userIds}
            />
          )}
          {companyIds.length > 0 && (
            <RuleSimpleCompanySelector
              allowReadOnlyOpen
              isReadOnly
              summaryCount={10}
              value={companyIds}
            />
          )}
          {!hasOptedInTargets && (
            <Text color="dimmed" fontSize="sm">
              No one yet
            </Text>
          )}
        </Flex>
        <Button
          as={Link}
          size="xs"
          to="../opt-in"
          type="button"
          variant="ghostDimmed"
        >
          {canEdit ? "Manage" : "View"}
        </Button>
      </Grid>
    </Card>
  );
}

const TARGETING_CHANGED_TOAST_TITLE = "Targets have changed";
const TARGETING_CHANGED_TOAST_DESCRIPTION =
  "Refresh the page and apply your changes again. The targets have changed since you first loaded the page, possibly by someone else.";

function isTargetingChangedConflict(error: AxiosError<ErrorResponse>) {
  return (
    error.response?.status === 409 &&
    error.response.data.error.message ===
      FEATURE_TARGETING_VERSION_CONFLICT_MESSAGE
  );
}

type FormProps = {
  feature: FeatureDetail;
  currentTargeting: FeatureTargetingVersion[];
  stages: StageDTO[];
};

const Form = ({ feature, stages, currentTargeting }: FormProps) => {
  const { app, env, org, userHasRole } = useEnvironment();
  const queryClient = useQueryClient();
  const confirmSaveDisclosure = useDisclosure();
  const requestFeedback = useRequestFeedback();
  const { data: notificationSettings } = useTargetingNotificationSettings();
  const { data: optInMemberships } = useFeatureOptInMembershipsData(feature.id);
  const { isEnabled: showEndUserOptInUi } = useFlag("end-user-opt-in-ui");

  const expandTargeting = useCallback(
    (targeting: EnvironmentFeatureTargeting[]) =>
      expandEnvironmentTargeting(app.environments, targeting),
    [app],
  );

  const incomingCurrentTargeting = useMemo(
    () => expandTargeting(currentTargeting),
    [expandTargeting, currentTargeting],
  );

  const [baselineTargeting, setBaselineTargeting] = useState(
    incomingCurrentTargeting,
  );
  const [baselineVersionIds, setBaselineVersionIds] = useState(() =>
    currentTargeting.map((v) => v.id),
  );

  const updateFeatureTargetingMutation = useFeatureTargetingUpdateMutation(
    feature.id,
    baselineVersionIds,
  );

  // Store targeting before potential stage change in the confirmation dialog
  const targetingSnapshot = useRef<UpdateEnvironmentFeatureTargeting[]>([]);

  const form = useForm<UpdateFeatureTargetingBody>({
    resolver: zodResolver(UpdateFeatureTargetingBodySchema),
    defaultValues: {
      stageId: feature.stage?.id,
      versions: baselineTargeting,
      changeDescription: "",
    },
  });

  useEffect(() => {
    if (form.formState.isDirty || confirmSaveDisclosure.isOpen) {
      return;
    }

    setBaselineTargeting(incomingCurrentTargeting);
    setBaselineVersionIds(currentTargeting.map((v) => v.id));
    form.reset({
      stageId: feature.stage?.id,
      versions: incomingCurrentTargeting,
      changeDescription: "",
    });
  }, [
    confirmSaveDisclosure.isOpen,
    currentTargeting,
    feature.stage?.id,
    form,
    form.formState.isDirty,
    incomingCurrentTargeting,
  ]);

  // Register the form with the unsaved changes context to use in the sidebar
  useUnsavedChangesRegister("targeting", form);

  const newStageId = useWatch({ control: form.control, name: "stageId" });
  const newVersions = useWatch({ control: form.control, name: "versions" });

  const notificationDefaultsInfo = useMemo(
    () =>
      getTargetingNotificationDefaultsInfo({
        app,
        feature,
        currentTargeting: baselineTargeting,
        newTargeting: newVersions,
        currentStageId: feature.stage?.id ?? null,
        newStageId: newStageId ?? null,
        orgIntegrations: org.integrations,
        notificationSettings,
      }),
    [
      app,
      baselineTargeting,
      feature,
      newStageId,
      newVersions,
      notificationSettings,
      org.integrations,
    ],
  );

  const onSaveClick = form.handleSubmit(({ versions, stageId }) => {
    const normalizedNewTargeting = expandTargeting(
      updateToEnvironmentFeatureTargeting(versions),
    );

    // Store the versions before showing the confirmation dialog
    targetingSnapshot.current = normalizedNewTargeting;
    form.setValue(
      "notifications",
      getDefaultNotificationProvidersForTargetingChange({
        app,
        feature,
        currentTargeting: baselineTargeting,
        newTargeting: normalizedNewTargeting,
        currentStageId: feature.stage?.id ?? null,
        newStageId: stageId ?? null,
        orgIntegrations: org.integrations,
        notificationSettings,
      }),
    );
    confirmSaveDisclosure.onOpen();
  });

  const onConfirmClick = useFormMutationSubmitHandler(
    form,
    updateFeatureTargetingMutation,
    (versions, variables) => {
      const newBaselineTargeting = expandTargeting(versions);
      setBaselineTargeting(newBaselineTargeting);
      setBaselineVersionIds(versions.map((v) => v.id));
      queryClient.setQueryData(
        featureQueryKeys.targeting(app.id, undefined, feature.id),
        versions,
      );
      form.reset({
        stageId: variables.stageId,
        versions: newBaselineTargeting,
        changeDescription: "",
      });
      confirmSaveDisclosure.onClose();
      queryClient.invalidateQueries({
        queryKey: featureQueryKeys.single(app.id, env.id, feature.id),
      });
    },
    {
      successToast: "Access rules updated",
      errorToast: (error) =>
        isTargetingChangedConflict(error)
          ? TARGETING_CHANGED_TOAST_TITLE
          : "Failed to update access rules",
      errorToastOptions: (error) =>
        isTargetingChangedConflict(error)
          ? {
              containerStyle: {
                marginBottom: 4,
                marginInline: 4,
                minWidth: 0,
              },
              description: TARGETING_CHANGED_TOAST_DESCRIPTION,
              duration: null,
              isClosable: true,
            }
          : {},
    },
  );

  const { isEnabled: enforceUserPermissions } = useFlag("user-permissions");
  const isContributor = userHasRole("contributor") || !enforceUserPermissions;
  const isPublisher = userHasRole("publisher") || !enforceUserPermissions;

  const onDialogStageChange = useCallback(
    (stageId: string) => {
      form.setValue(
        "notifications",
        getDefaultNotificationProvidersForTargetingChange({
          app,
          feature,
          currentTargeting: baselineTargeting,
          newTargeting: form.getValues("versions"),
          currentStageId: feature.stage?.id ?? null,
          newStageId: stageId,
          orgIntegrations: org.integrations,
          notificationSettings,
        }),
      );
    },
    [
      app,
      baselineTargeting,
      feature,
      form,
      notificationSettings,
      org.integrations,
    ],
  );

  const onClose = useCallback(() => {
    // Reset the stage and change description fields
    form.resetField("stageId");
    form.resetField("changeDescription");
    form.resetField("notifications");
    // Restore the versions before closing the confirmation dialog
    form.setValue("versions", targetingSnapshot.current);
    confirmSaveDisclosure.onClose();
  }, [confirmSaveDisclosure, form, targetingSnapshot]);

  const { flagVersion, flagVersionIndex } = useMemo(() => {
    const index = baselineTargeting.findIndex(
      (v) => v.environmentId === env.id,
    );
    return {
      flagVersion: baselineTargeting[index],
      flagVersionIndex: index,
    };
  }, [baselineTargeting, env]);

  const currentEnvironmentTargeting =
    (newVersions ?? []).find((version) => version.environmentId === env.id) ??
    flagVersion;

  const canEdit =
    !feature.isArchived &&
    ((env.isProduction && isPublisher) || (!env.isProduction && isContributor));
  const activeOptInMemberships =
    showEndUserOptInUi &&
    optInMemberships &&
    feature.optInEnabled &&
    currentEnvironmentTargeting?.targetingMode !== "none"
      ? optInMemberships
      : undefined;
  const optInTargetsContent =
    currentEnvironmentTargeting?.targetingMode === "some" &&
    activeOptInMemberships ? (
      <OptInTargetsFrame
        canEdit={canEdit}
        companyIds={activeOptInMemberships.companyIds}
        userIds={activeOptInMemberships.userIds}
      />
    ) : undefined;

  return (
    <FormProvider {...form}>
      <Flex
        alignItems="flex-start"
        as="form"
        direction="column"
        gap={4}
        w="full"
        onSubmit={onSaveClick}
      >
        <SectionHeading
          _container={{ minH: 8 }}
          rightElement={
            <Button
              colorScheme="brand"
              variant="ghost"
              onClick={(e) => {
                e.preventDefault();
                requestFeedback({
                  flagKey: "access-simplification",
                  title: "How do you like the Access tab?",
                  position: {
                    type: "POPOVER",
                    anchor: e.currentTarget,
                  },
                  openWithCommentVisible: true,
                });
              }}
            >
              Give feedback
            </Button>
          }
        >
          Current targeting
        </SectionHeading>

        {canEdit ? (
          <>
            <EnvironmentForm
              baseName={`versions.${flagVersionIndex}`}
              additionalRuleContent={optInTargetsContent}
              control={form.control}
              feature={feature}
              optInMemberships={activeOptInMemberships}
            />
            <ButtonGroup>
              <FormSubmit
                isLoading={updateFeatureTargetingMutation.isPending}
                disableOnPristine
                showTooltipOnPristine
              />
              <FormReset />
            </ButtonGroup>
          </>
        ) : (
          <RulesDisplay
            additionalRuleContent={optInTargetsContent}
            flagKey={feature.key}
            lockReason={`You don't have permission to modify ${env.name} environment's targeting rules.`}
            optInMemberships={activeOptInMemberships}
            targeting={flagVersion}
          />
        )}

        <FeatureTargetingChangeConfirmationDialog
          currentTargeting={baselineTargeting}
          feature={feature}
          isLoading={updateFeatureTargetingMutation.isPending}
          isOpen={confirmSaveDisclosure.isOpen}
          newStageId={newStageId ?? null}
          newTargeting={newVersions}
          notificationDefaultsInfo={notificationDefaultsInfo}
          stages={stages}
          targetingSnapshot={targetingSnapshot.current}
          canChangeStage
          onStageChange={onDialogStageChange}
          onCancel={onClose}
          onClose={onClose}
          onConfirm={onConfirmClick}
        />
      </Flex>

      <FeatureTargetingHistory environmentId={env.id} feature={feature} />
    </FormProvider>
  );
};

type FeatureTargetingFormProps = {
  feature: FeatureDetail;
};

export const FeatureTargetingForm = ({
  feature,
}: FeatureTargetingFormProps) => {
  const {
    data: targeting = [],
    isLoading: isTargetingLoading,
    isSuccess: isTargetingSuccess,
  } = useFeatureTargetingData(feature.id);

  const { data: stages = [], isLoading: isStagesLoading } = useStagesData();

  if (isTargetingLoading || isStagesLoading) {
    return (
      <Center flexGrow={1}>
        <DelayedSpinner />
      </Center>
    );
  }

  return (
    <Flex alignItems="flex-start" direction="column" gap={4}>
      <Restricted
        alertStyle="callout"
        displayContents="normal"
        feature={feature}
        role="contributor"
      >
        {isTargetingSuccess ? (
          <Form
            currentTargeting={targeting}
            feature={feature}
            stages={stages}
          />
        ) : (
          <Text>Error loading flag</Text>
        )}
      </Restricted>
    </Flex>
  );
};
